export const CHECKOUT_URL = 'https://neurology-visual-atlas.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '1005118822521193'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/gED88kzrFztTsLVuNh3mrd/Diseno-sin-titulo-11.webp'

export interface InsideImage {
  src: string
  alt: string
}

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/jfcYiHM5T2YSJC7YuDkm3v/Captura-de-pantalla-2026-09-26-a-la_s_-7.36.40_p._m..png',
    alt: 'Dorsal column vs spinothalamic — pathway comparison page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/vruzoEhQWMnh7uij1Zd2nV/Captura-de-pantalla-2026-09-26-a-la_s_-7.36.25_p._m..png',
    alt: 'Corticobulbar pathways — central vs peripheral facial pattern page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/59v9bvdwFAmQMbjuz75shi/Captura-de-pantalla-2026-09-26-a-la_s_-7.37.49_p._m..png',
    alt: 'Lesion localization by reflex pattern — reflex-root reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/mUfohbcCyqS8F6ZxRrAsRz/Captura-de-pantalla-2026-09-26-a-la_s_-7.36.56_p._m..png',
    alt: 'Cerebellar pathways and functional connections page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/xpgb6oXZ3eKtxCwV3F6bbu/Captura-de-pantalla-2026-09-26-a-la_s_-7.36.06_p._m..png',
    alt: 'The nervous system as a connected network page',
  },
]

export const systemBullets = [
  'Neuroanatomy & functional mapping',
  'Neural pathways & lesion localization',
  'Cranial nerves & brainstem',
  'Neurological signs & clinical patterns',
  'Neurological disorders & pattern recognition',
  'Neurological assessment & clinical reasoning',
]

export type ModuleColor = 'blue' | 'red' | 'amber' | 'green' | 'purple' | 'charcoal'

export interface Module {
  number: number
  title: string
  body: string
  steps: string
  color: ModuleColor
}

export const modules: Module[] = [
  {
    number: 1,
    title: 'NEUROANATOMY & FUNCTIONAL MAPPING',
    body: 'Build a clear visual understanding of the nervous system and its functional organization. Explore cerebral lobes, cortical areas, basal ganglia, thalamus, cerebellum, brainstem, spinal cord and major structures through organized maps, diagrams and functional references.',
    steps: 'Locate. Visualize. Understand.',
    color: 'blue',
  },
  {
    number: 2,
    title: 'NEURAL PATHWAYS & LESION LOCALIZATION',
    body: 'Follow major neurological pathways and understand how lesion location can change the clinical pattern. Explore motor, sensory, corticospinal, spinothalamic, dorsal column and cerebellar pathways through visual maps, crossing points and structured localization references.',
    steps: 'Follow. Connect. Localize.',
    color: 'red',
  },
  {
    number: 3,
    title: 'CRANIAL NERVES & BRAINSTEM',
    body: 'Understand the anatomy, functions and clinical relationships of the 12 cranial nerves. Explore cranial nerve pathways, nuclei, functions, examination findings and brainstem relationships through organized visual maps and comparison references.',
    steps: 'Identify. Compare. Connect.',
    color: 'amber',
  },
  {
    number: 4,
    title: 'NEUROLOGICAL SIGNS & CLINICAL PATTERNS',
    body: 'Recognize important neurological findings and understand what they may indicate anatomically. Explore weakness, sensory deficits, reflexes, coordination, abnormal movements, gait patterns, speech changes and other findings through structured visual comparisons.',
    steps: 'Observe. Compare. Understand.',
    color: 'green',
  },
  {
    number: 5,
    title: 'NEUROLOGICAL DISORDERS & PATTERN RECOGNITION',
    body: 'Organize major neurological disorders into recognizable study patterns. Explore stroke syndromes, epilepsy, headaches, movement disorders, demyelinating conditions, neuropathies, neurodegenerative conditions and other high-yield presentations.',
    steps: 'Recognize. Differentiate. Review.',
    color: 'purple',
  },
  {
    number: 6,
    title: 'NEUROLOGICAL ASSESSMENT & CLINICAL REASONING',
    body: 'Bring anatomy, pathways, signs and patterns together into a structured reasoning process. Work through neurological assessment, symptom-to-structure relationships, localization workflows, comparison charts and educational clinical scenarios.',
    steps: 'Assess. Connect. Reason.',
    color: 'charcoal',
  },
]

export interface Bonus {
  number: number
  title: string
  subtitle: string
  body: string
  steps: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'NEUROLOGICAL EXAMINATION VISUAL QUICK GUIDE™',
    subtitle: 'Organize the Neurological Examination Visually',
    body: 'Quickly review mental status, cranial nerves, motor function, sensory testing, reflexes, coordination and gait through structured visual references.',
    steps: 'Review. Examine. Connect.',
  },
  {
    number: 2,
    title: 'NEUROLOGICAL LOCALIZATION WORKFLOW MAPS™',
    subtitle: 'Turn Neurological Findings Into Anatomical Clues',
    body: 'Follow structured visual pathways to compare cortical, brainstem, spinal cord, nerve root, peripheral nerve and other localization patterns.',
    steps: 'Observe. Connect. Localize.',
  },
  {
    number: 3,
    title: 'CRANIAL NERVE VISUAL REFERENCE PACK™',
    subtitle: 'See the 12 Cranial Nerves as One Connected System',
    body: 'Review cranial nerve functions, pathways, nuclei, eye movements, facial patterns, reflex circuits and brainstem relationships through organized visual references.',
    steps: 'Visualize. Compare. Review.',
  },
  {
    number: 4,
    title: 'NEUROLOGICAL PATTERN COMPARISON CHARTS™',
    subtitle: 'Compare Similar Neurological Findings Side by Side',
    body: 'Differentiate motor, sensory, movement, gait, language, cranial nerve and localization patterns through structured comparison charts.',
    steps: 'Compare. Differentiate. Understand.',
  },
  {
    number: 5,
    title: 'NEUROLOGY CLINICAL REASONING CASEBOOK™',
    subtitle: 'Practice Connecting Findings, Anatomy and Localization',
    body: 'Work through guided educational cases designed to help you organize neurological findings, identify anatomical clues and practice structured reasoning.',
    steps: 'Analyze. Connect. Reason.',
  },
]

export interface Differentiator {
  icon: string
  title: string
  body: string
}

export const differentiators: Differentiator[] = [
  {
    icon: '🧠',
    title: 'Practical visual references',
    body: 'Find the structure, pathway, sign or pattern you need while studying neurology.',
  },
  {
    icon: '🔍',
    title: 'Structured learning system',
    body: 'Move from anatomy to pathways, findings, localization and clinical reasoning.',
  },
  {
    icon: '⚡',
    title: '6 neurological areas',
    body: 'Neuroanatomy, pathways, cranial nerves, clinical signs, disorders and assessment.',
  },
  {
    icon: '🔄',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever you need to study, compare or review a neurological concept.',
  },
]

export const offerBullets = [
  '6 comprehensive modules',
  '5 strategic bonuses',
  'Neuroanatomy & functional mapping',
  'Neural pathways & lesion localization',
  'Cranial nerves & clinical patterns',
  'Neurological disorders & assessment',
  'Structured clinical reasoning',
]

export interface Testimonial {
  name: string
  role: string
  location: string
  quote: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Hannah Mitchell, 23',
    role: 'Medical student, 2nd year',
    location: 'Boston, United States',
    quote:
      'Neuroanatomy was the block I dreaded most. I could label the pathways but had no idea how a lesion changed the picture. The dorsal column vs spinothalamic pages finally made crossing points click, and I can now look at a sensory deficit and say where the lesion probably is. My neuro block exam was my best score of the year.',
  },
  {
    name: 'Marcus Chen, 26',
    role: 'Medical student, neurology rotation',
    location: 'Toronto, Canada',
    quote:
      'On rotation my attending kept asking me to localize before suggesting any imaging. I was lost. The localization workflow maps give me the same order every time: cortex, brainstem, cord, root, nerve. After a week I was presenting cases with a clear localization and the feedback changed completely.',
  },
  {
    name: 'Olivia Bennett, 21',
    role: 'Neuroscience student, 3rd year',
    location: 'Edinburgh, United Kingdom',
    quote:
      'The 12 cranial nerves were a list I kept forgetting. The cranial nerve reference pack shows nuclei, pathways and exam findings together, so it feels like one system instead of twelve facts. The central vs peripheral facial pattern page alone saved me hours of rereading.',
  },
  {
    name: 'Sophie Walker, 24',
    role: 'Physician associate student',
    location: 'Sydney, Australia',
    quote:
      'I study between placements and needed something organized I could open on my phone. The comparison charts put similar findings side by side, like gait patterns and reflex changes, and the reflex-root table is now my go-to before every neuro exam station. Revision feels structured instead of overwhelming.',
  },
  {
    name: 'Ethan Rodriguez, 28',
    role: 'Nursing student moving into neuro ICU',
    location: 'Phoenix, United States',
    quote:
      'I knew neuro checks but not why the findings mattered. The clinical reasoning casebook walks through findings, anatomy and localization step by step. Now when a patient has new weakness or a pupil change, I understand what structure could be involved and what to report.',
  },
]

export interface Faq {
  q: string
  a: string
}

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Neurology students, medical students, healthcare students and learners studying neurological anatomy, assessment and clinical patterns.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. NEUROLOGY VISUAL ATLAS™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a visual educational and study reference system.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. It focuses on organized visual references, anatomical maps, pathway diagrams, pattern comparisons and structured reasoning tools.',
  },
  {
    q: 'What areas are included?',
    a: 'Neuroanatomy, neural pathways, lesion localization, cranial nerves, neurological signs, clinical patterns, major disorders, assessment and clinical reasoning.',
  },
  {
    q: 'Does this replace formal medical education?',
    a: 'No. It is an educational study resource and does not replace formal medical education, supervised clinical training, institutional guidance or professional judgment.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'What devices can I use it on?',
    a: 'Any device that opens PDF files — phone, tablet, laptop or desktop. You can also print the maps and charts you use most.',
  },
  {
    q: 'Do I need previous neurology knowledge?',
    a: 'No. The system starts with neuroanatomy and functional mapping, then moves into pathways, cranial nerves, signs, disorders and clinical reasoning, so both early and advanced learners can use it.',
  },
  {
    q: 'How long do I keep access, and what if it is not for me?',
    a: 'Access is yours to keep and review whenever you need it. If it is not right for you, contact us within 7 days for a full refund according to the refund terms.',
  },
]

export interface PopupSale {
  name: string
  city: string
  country: string
}

export const popupSales: PopupSale[] = [
  { name: 'Emily W.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'James C.', city: 'Toronto', country: 'Canada' },
  { name: 'Sophia M.', city: 'Austin', country: 'United States' },
  { name: 'Oliver B.', city: 'Melbourne', country: 'Australia' },
  { name: 'Grace T.', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Ethan R.', city: 'Vancouver', country: 'Canada' },
  { name: 'Ava S.', city: 'Chicago', country: 'United States' },
  { name: 'Liam H.', city: 'Brisbane', country: 'Australia' },
  { name: 'Charlotte D.', city: 'Bristol', country: 'United Kingdom' },
  { name: 'Noah P.', city: 'Calgary', country: 'Canada' },
  { name: 'Isabella R.', city: 'Miami', country: 'United States' },
  { name: 'Jack M.', city: 'Perth', country: 'Australia' },
]
